import React, {useEffect, useRef, useState} from "react";
import {
  Card,
  FlexItem,
  FlexLayout, FormField,
  FormFieldHelperText,
  FormFieldLabel,
  Input,
  Link, Spinner,
  StackLayout,
  Text
} from "@salt-ds/core";
import CardAlert from "../../components/common/card-alert/CardAlert";
import CardHeader from "../../components/common/card-header/CardHeader";
import Constants from "../../components/common/Constants";
import ChangePasswordComponent from "../../components/common/ChangePasswordComponent";
import PageNavigationCustom from "../../components/common/page-navigation/PageNavigationCustom";
import {HiddenSolidIcon, VisibleIcon} from "@salt-ds/icons";
import {useLocation, useNavigate} from "react-router";
import {TargetRedirectComponent} from "../TargetRedirect/TargetRedirectComponent.tsx";
import {useIntl} from 'react-intl';

function ChangeExpiringPasswordPage() {
  const formRef = useRef();
  const [hasError, setHasError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [currentPasswordError, setCurrentPasswordError] = useState(false);
  const [currentPasswordErrorMessage, setCurrentPasswordErrorMessage] = useState("");
  const [message, setMessage] = useState("");
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);
  const [showRedirect, setShowRedirect] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const forgotPasswordLink = <Link href="/auth/public/app/forgotPassword"
    className="links"
    aria-label={Constants.forgotPasswordAdaLabel}>{Constants.homePageLoginCardForgotPasswordLabel}</Link>;
  const location = useLocation();
  const {passwordExpiryData} = location.state || {passwordExpiryData: {daysUntilNextReminder: 90}};
  const showErrorOnPage = (error) => {
    setMessage(error);
    setHasError(true);
  }

  const [showAlertBox, setShowAlertBox] = useState(false);
  const navigate = useNavigate();
  const intl = useIntl();
  const mainRef = useRef(null);

  useEffect(() => {
    document.title = intl.formatMessage({id: 'logon.ui.ChangePassword.page.title'});
    if (mainRef.current) {
      mainRef.current.focus();
    }
    if (location.state?.from !== Constants.fromPasswordExpiringModal ) {
      navigate('/');
    }
  }, [location, navigate]);

  const showSuccessOnPage = (message) => {
    setHasError(false);
    setShowAlertBox(true);
    setMessage(message);
    setIsLoading(false);
  }

  const handlePasswordChange = (event) => {
    const value = event.target.value;
    setCurrentPassword(value);
    value && setCurrentPasswordError(false);
  };

  const handleNextButtonClick = () => {
    setIsLoading(true);
    if (showAlertBox || passwordChangeSuccess) {
      setShowRedirect(true);
      return;
    }

    if (formRef.current) {
      formRef.current.submitForm();
    }
  }

  const handleCurrentPasswordFieldEmptyError = (isError) => {
    setCurrentPasswordError(isError);
  }

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  function StatusDisplay({
    hasError,
    showRedirect,
    isLoading,
    setHasError,
    showAlertBox,
    setShowAlertBox,
    message
  }) {
    if (showRedirect) {
      return (
        <FlexItem className="spinner-wrapper">
          <Spinner data-testid="spinner-loading" aria-label="redirecting" role="status" size="medium" />
          <TargetRedirectComponent />
        </FlexItem>
      );
    } else if (isLoading) {
      return (
        <FlexItem className="spinner-wrapper">
          <Spinner data-testid="spinner-loading" aria-label="loading" role="status" size="medium" />
        </FlexItem>
      );
    } else {
      return (
        <FlexItem className={`error-wrapper ${hasError || showAlertBox ? 'visible' : ''}`}>
          <div>
            <CardAlert setShowAlertBox={setShowAlertBox}
              status={hasError ? "error" : "success"}
              setHasError={setHasError}>
              {message}
            </CardAlert>
          </div>
        </FlexItem>
      )
    }
  }

  return (
    <main ref={mainRef} role="main" aria-label="Change Expiring Password" tabIndex="-1">
    <FlexLayout className={"page-content"}>
      <StatusDisplay
        hasError={hasError}
        showRedirect={showRedirect}
        isLoading={isLoading}
        setHasError={setHasError}
        showAlertBox={showAlertBox}
        setShowAlertBox={setShowAlertBox}
        message={message}
      />

      <FlexItem>
        <Card className={"card-wrapper"}>
          <FlexItem className="card-content">
            <StackLayout gap={3}>

              <CardHeader style={{maxWidth: '16.5rem'}} heading={Constants.expiringPasswordChange} />
              <FlexItem>
                <Text>
                  {Constants.expiringChangePasswordInstructionalText(passwordExpiryData.daysUntilNextReminder)}
                </Text>
              </FlexItem>
              <fieldset disabled={passwordChangeSuccess} style={{border:0, padding:0, margin:0}}>
                <FlexItem className={"form-layout"}>
                  <FormField
                    validationStatus={currentPasswordError ? "error" : undefined}>
                    <FormFieldLabel>Current Password</FormFieldLabel>
                    <Input id="password" name="password" inputProps={{
                      type: showPassword ? "text" : "password",
                      maxLength: 25,
                      "aria-required": "true",
                      "aria-describedby": currentPasswordError ? "currentPassword-error" : undefined,
                    }}
                      value={currentPassword}
                      onChange={handlePasswordChange}
                      endAdornment={
                        showPassword
                            ? <VisibleIcon tabIndex={0} onKeyPress={togglePasswordVisibility} onClick={togglePasswordVisibility}
                              role="button"
                              aria-label="Hide password"
                          />
                            : <HiddenSolidIcon tabIndex={0} onKeyPress={togglePasswordVisibility} onClick={togglePasswordVisibility}
                              role="button"
                              aria-label="Show password"
                          />
                      }/>
                    {currentPasswordError &&
                      <FormFieldHelperText id="currentPassword-error" role="alert">{currentPasswordErrorMessage}</FormFieldHelperText>}
                  </FormField>
                </FlexItem>
                <ChangePasswordComponent
                  setCurrentPasswordErrorMessage={setCurrentPasswordErrorMessage}
                  setCurrentPasswordError={handleCurrentPasswordFieldEmptyError}
                  currentPassword={currentPassword}
                  setPasswordChangeSuccess={setPasswordChangeSuccess}
                  showSuccessOnPage={showSuccessOnPage}
                  showErrorOnPage={showErrorOnPage}
                  redirectUrl={Constants.forgotPasswordSuccess}
                  ref={formRef}
                  isExpiringChangePasswordFlow={true}
                  isDisabled={passwordChangeSuccess}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                />
                  
              </fieldset>
              <PageNavigationCustom isCancelNotAllowed={false}
                nextButtonText={passwordChangeSuccess ? "CONTINUE" : "SUBMIT"}
                previousPage={"indexPage"}
                onSubmit={handleNextButtonClick}
                isLoading={isLoading}/>
            </StackLayout>
          </FlexItem>
        </Card>
      </FlexItem>
    </FlexLayout>
    </main>
  );
}

export default ChangeExpiringPasswordPage;